/**
 * 场景卡 preset(B2 §2.0 横切)—— 卡片 = 参数预设路由的「参数」半边。
 *
 * ScenePreset 是 discriminated union(kind = sceneKey 字面量;长图族 10 个 sceneKey
 * 共用一个 kind='longimage' + usage 字段,sceneKey 由 sceneKeyOf 派生),配唯一验型器
 * parseScenePreset:各创建页只 import 这一个验型器消费 location.state.scenePreset,
 * 禁止各页手写校验(防多套漂移)。非法 state 整包丢弃返回 null(页面按无预设渲染)。
 *
 * 传参约定(§3.1 拍板):navigate(route, { state: { scenePreset } }),不进 URL query。
 * 消费侧一次性 ref 消费 + clearSceneCardState() 清 history state(刷新不再重放预设)。
 */

import { type BriefPurpose, isBriefPurpose, videoSceneKeyOf } from "./briefPurpose";

/** 长图 10 用途(单一源;features/poster blocks.ts 的 UseCase 别名到这里)。 */
export const LONG_IMAGE_USAGES = [
  "goods",
  "corp",
  "event",
  "review",
  "report",
  "hire",
  "menu",
  "tutorial",
  "rank",
  "social",
] as const;

export type LongImageUsage = (typeof LONG_IMAGE_USAGES)[number];

/**
 * 横宽单帧版式(C3;features/poster blocks.ts 的 WIDE_LAYOUTS 别名到这里)。
 * 与 ai compose_wide_frame 的分区数表同源:lr/tb=2 区、center=1 区、three=3 区。
 */
export const WIDE_LAYOUT_KEYS = ["lr", "tb", "center", "three"] as const;
export type WideLayoutKey = (typeof WIDE_LAYOUT_KEYS)[number];

/** 横宽 8 尺寸预设 key(尺寸值在 blocks.ts WIDE_PRESETS;此处仅收窄验型)。 */
export const WIDE_PRESET_KEYS = [
  "banner",
  "mp",
  "vcover",
  "live",
  "webhero",
  "kv",
  "ppt",
  "outdoor",
] as const;
export type WidePresetKey = (typeof WIDE_PRESET_KEYS)[number];

/** 套图可选画幅(与海报 free-create Ratio 5 档同字面量;此处仅收窄验型)。 */
export const PRESET_RATIOS = ["1:1", "4:5", "3:4", "9:16", "16:9"] as const;
export type PresetRatio = (typeof PRESET_RATIOS)[number];

export interface PresetGroupPoint {
  title: string;
  detail?: string;
}

/** 新闻舱体裁六种(与后端 ReporterDraftService.GENRES 白名单同字面量,新增/改名以后端为准)。 */
export const REPORTER_GENRES = [
  "COMPANY_NEWS",
  "PRODUCT_COPY",
  "XIAOHONGSHU",
  "EVENT_RELEASE",
  "BRAND_PROFILE",
  "QA_ARTICLE",
] as const;
export type ReporterPresetGenre = (typeof REPORTER_GENRES)[number];

/**
 * 体裁 → sceneKey(卡 key / 任务打标 / works 场景筛三处同源,别处不许再写一份映射)。
 */
export const REPORTER_GENRE_SCENE_KEY: Record<ReporterPresetGenre, string> = {
  COMPANY_NEWS: "reporter_news",
  PRODUCT_COPY: "reporter_product",
  XIAOHONGSHU: "reporter_xhs",
  EVENT_RELEASE: "reporter_event",
  BRAND_PROFILE: "reporter_brand",
  QA_ARTICLE: "reporter_qa",
};

/**
 * S2 渠道 → 体裁映射(雷达 30 篇计划送官媒;实施计划 S2-T1 口径:知乎/公众号→产品软文、
 * 官网→品牌介绍、百家号/垂直媒体→企业新闻、小红书→种草笔记)。子串匹配容渠道后缀
 * (如「知乎回答」);未知渠道兜底企业新闻(最通用体裁,不拒送)。单源:挨着
 * REPORTER_GENRE_SCENE_KEY,别处不许再写一份映射。
 */
export const REPORTER_CHANNEL_GENRE: Record<string, ReporterPresetGenre> = {
  // R5(2026-08-10):知乎是问答平台,改配 GEO 问答文(问答长尾是 GEO 核心体裁);
  // 问答类渠道词一并进问答文。
  知乎: "QA_ARTICLE",
  问答: "QA_ARTICLE",
  公众号: "PRODUCT_COPY",
  官网: "BRAND_PROFILE",
  百家号: "COMPANY_NEWS",
  垂直媒体: "COMPANY_NEWS",
  小红书: "XIAOHONGSHU",
};

export function reporterGenreOfChannel(channel: string | undefined | null): ReporterPresetGenre {
  const c = (channel ?? "").trim();
  for (const [key, genre] of Object.entries(REPORTER_CHANNEL_GENRE)) {
    if (c.includes(key)) return genre;
  }
  return "COMPANY_NEWS";
}

/**
 * 跨舱 handoff 素材种子(S2:雷达内容计划 / 追爆拆解 → 官媒创建页)。
 * 只进素材框**可编辑**,不静默直发 —— 各字段拼成素材文本由创建页做。
 */
export interface ReporterMaterialSeed {
  title?: string;
  userQuestion?: string;
  channel?: string;
  audience?: string;
  keywords?: string[];
  /**
   * 自由文本素材正文(S2 追爆→官媒:钩子/结构骨架/口播脚本/字幕文案等拆解产出装这里)。
   * 与其余结构化字段一样只预填素材框,可编辑不直发。
   */
  body?: string;
}

/**
 * BPWB-2(2026-09-03):写法四格(全片结构/情绪基调/开头手法/结尾手法)结构化展示数据 ——
 * 蓝图工作卡 structure 插槽消费,只做展示。
 * 2026-10-10 起写法不再拼进素材文本上送(P5:素材会被 fact_guard 当证据),改由 C3 `borrow`
 * 字段独立上送(entities/explosive/model/reporterBorrow)。
 */
export interface ReporterBlueprintCraft {
  structure?: string;
  emotion?: string;
  /** 开头手法的参考原话(仅作例子,不是「内容」红线要挡的东西 —— 手法本就该带例句)。 */
  hookExample?: string;
  /** 结尾手法的参考原话,同上。 */
  ctaExample?: string;
}

/**
 * S2 官媒→长图种子(标题 + 分段文案带入搭建器)。体裁边界:记者=文优先(成稿是文章),
 * 长图=图优先(文案变区块文字位,画面由用户在搭建器补)—— 种子只铺文字,不铺图。
 */
export interface LongImageReporterSeed {
  title: string;
  paragraphs: string[];
}

/**
 * AI 模特换衣的**任务打标 key**(2026-08-12)。
 *
 * ★为什么它与卡 key(`tryon`)不同名:后端 mediaTask 那侧的 sceneKey 跟着模块名走
 *   (`ai_tryon`,方案 §3),而卡 key 同时又是三皮肤主角图 / 样图的**文件名**
 *   (`/scene-figures/<skin>/tryon.webp`)—— 两边各有各的命名法,硬统一必然有一边别扭。
 * ★所以让它们**显式分开、单源在这里**:卡上用 `SceneCard.sceneKey` 登记打标 key,
 *   `sceneKeyOf` 与三步页提交体都取这一个常量。全仓只有这一处字面量。
 */
export const TRYON_SCENE_KEY = "ai_tryon";

export type ScenePreset =
  /** 商品主图:走创建页默认态(单张 T2I)。 */
  | { kind: "poster_single" }
  /**
   * AI 模特换衣(2026-08-12):模特照 + 平铺服装图 → 专项试衣模型换衣。
   * 无参 —— 模特/服装都是页内上传的对象,不适合塞进 history state 里带走。
   */
  | { kind: "tryon" }
  /** 爆点组图:创建页开组图模式;groupPoints 允许空模板(用户手填爆点)。 */
  | { kind: "poster_group"; groupPoints: PresetGroupPoint[] }
  /** 全渠道套图:创建页预选多画幅。 */
  | { kind: "poster_multiratio"; ratios: PresetRatio[] }
  /**
   * 详情长图 10 用途:搭建器整套默认区块 = skeletonFor(usage)。
   * reporterSeed(S2 官媒→长图):标题+分段文案铺进区块文字位(优先于默认骨架)。
   */
  | { kind: "longimage"; usage: LongImageUsage; reporterSeed?: LongImageReporterSeed }
  /** 横宽宽图(C3):同一搭建器的横宽模式 —— 版式模板决定分区 + 8 尺寸预设。 */
  | { kind: "poster_wide"; wideLayout: WideLayoutKey; widePreset: WidePresetKey }
  /**
   * 视频目的卡(波1 = 8 目的卡墙)。
   *
   * ★取代原来的 `video_narrative` / `video_hardsell` 两个无参 kind:那两个 kind 本质就是
   * 「预填某个 `briefPurpose`」,却把目的编码进了 kind 名 —— 8 个目的就要 8 个 kind,
   * 每加一档都要动 union + 验型 switch + 消费页 if 三处。改成带参一个 kind 后,
   * 加/改目的只动 `briefPurpose.ts` 单一源。
   * pre-launch 不留旧 kind 兼容分支(方案 D7)。
   */
  | { kind: "video"; purpose: BriefPurpose }
  /**
   * 分身(B5 接线)。`presetAvatarId`=公共形象点选直达;`presetVoice`=声音库「用 TA 出片」。
   * ★三条线都用音色,所以三个 kind 都收 `presetVoice`(2026-08-08 IA 收尾)——
   *   此前只有 `dh_talking` 收,声音库无论去哪条线都只能带 `dh_talking`,
   *   于是纯音频任务被打上 `dh_talking` 的 sceneKey,`/works` 场景筛就此筛错。
   * ★`voiceProfileId`(2026-09-08 声音 V2):声音库「我的声纹」卡的「用 TA 出片」——
   *   与 `presetVoice` 互斥(一次只点一张卡),带它时落地页选中「我的声纹」而非预置音色。
   */
  | { kind: "dh_talking"; presetAvatarId?: string; presetVoice?: string; voiceProfileId?: number }
  | { kind: "dh_tts"; presetVoice?: string; voiceProfileId?: number }
  /**
   * 照片数字人(E1):落地默认态 = 长口播档(liveportrait,不限时长、20 积分/秒)。
   * 引擎侧 `avatarSource=upload_photo` 自 A 阶段就在,本 preset 只是路由参数。
   *
   * ★`presetAvatarId`(2026-08-08 IA 收尾):公共形象点选直达带的那一位。
   *   预置形象库**是照片资产** —— 形象选择弹框里「预置形象」tab 只在照片档出现
   *   (`DhAvatarPicker` filmType==="photo")。此前 hub / 形象库点公共形象带的是
   *   `dh_talking`,落到真人口播线上根本没有这个来源 tab:预选进去了却看不见也换不掉。
   *   带 id 时落「预置形象」口,不带时才是原来的「上传照片」默认口。
   */
  | { kind: "dh_photo"; presetAvatarId?: string; presetVoice?: string; voiceProfileId?: number }
  | { kind: "dh_from_video"; script: string }
  /**
   * 官媒舱(R2):体裁进创建页;subType 为品牌介绍四子类(中文名或英文码均可,创建页归一成英文码再发;
   * 后端按 COMPANY_INTRO 等四码白名单);materialSeed 为 S2 跨舱 handoff 预填素材(可编辑)。
   */
  | {
      kind: "reporter";
      genre: ReporterPresetGenre;
      subType?: string;
      materialSeed?: ReporterMaterialSeed;
      /** S2 来源条文案(如「来自声量雷达·第2月计划」);仅展示,不进任何请求。 */
      sourceLabel?: string;
      /** 批三「转渠道稿」溯源:由哪篇稿转出(进 createDraft 落库,后端校验存在性)。 */
      sourceDraftId?: number;
      /**
       * 0813:这篇稿的写法来自哪条爆款蓝图 —— 只随 createDraft 送后端做取用登记
       * (used_dests.reporter+1),不落草稿行、不进任何提示词。不带它蓝图卡永远「已用 0 次」。
       */
      blueprintId?: number;
      /**
       * 2026-08-13:蓝图 payload —— **只给「这次带给 AI 的原文」面板做只读展示**,
       * 不上送、不进提示词(与 blueprintId 只做登记同理)。
       * 新闻舱这条路上,内容层早就只交结构不交原话(见 reporterHandoff 文件头);
       * 这块补的是「结构层到底带了什么」的可见性,不改上送口径。
       */
      blueprintPayload?: import("@/entities/explosive").FormulaPayload;
      /** 2026-08-14:拆解报告,只读展示用(「参考片在拍什么」),不上送、不进提示词。 */
      blueprintBreakdown?: import("@/entities/explosive").ExplosiveBreakdown;
      /**
       * BPWB-2(2026-09-03):蓝图工作卡头行「学「X」的写法」+「来自 抖音」标签用 ——
       * 与 blueprintPayload/blueprintBreakdown 同源(都来自路由那条蓝图),只读展示,不上送。
       */
      formulaName?: string;
      sourcePlatform?: string;
      /**
       * BPWB-2:写法四格结构化数据,见 `ReporterBlueprintCraft` 头注。
       * ★与 materialSeed 同一条 landmine:字段一旦出现就必须至少一格非空 ——
       * `parseReporterSeed` 那条 `Object.keys(out).length ? out : null` 的姿势在下面
       * `parseBlueprintCraft` 里照抄了一份,给了空对象会把整个 reporter 分支判形状不对、
       * 连 blueprintId 都一并丢弃。生产侧(`reporterHandoff.buildReporterPreset`)本就只在
       * 真有内容时才带这个字段,不会踩到。
       */
      blueprintCraft?: ReporterBlueprintCraft;
    };

export type ScenePresetKind = ScenePreset["kind"];

/**
 * 任务打标用 sceneKey(§3.4)。带参 kind 的两族由参数派生,其余 kind 即 sceneKey:
 *   长图族 → `longimage_<usage>` · 视频目的卡 → `video_<purposeKey>`
 */
export function sceneKeyOf(preset: ScenePreset): string {
  if (preset.kind === "longimage") return `longimage_${preset.usage}`;
  if (preset.kind === "video") return videoSceneKeyOf(preset.purpose);
  if (preset.kind === "reporter") return REPORTER_GENRE_SCENE_KEY[preset.genre];
  // 换衣是唯一「打标 key ≠ kind」的无参 kind(见 TRYON_SCENE_KEY 头注)
  if (preset.kind === "tryon") return TRYON_SCENE_KEY;
  return preset.kind;
}

const RATIO_SET = new Set<string>(PRESET_RATIOS);

/**
 * 「这个比例送进 poster_multiratio 会被收下吗」——**验型器自己用的那把尺子**。
 *
 * ★导出它是因为送 preset 的一侧必须能**先自己量一遍**:`parseScenePreset` 对
 *   poster_multiratio 是整包丢弃(有一个不在这 5 档 → 返回 null),而调用方拿到 null
 *   只会「按无预设渲染」,看起来跟功能没做一模一样。详情页「重新创作」就栽在这:
 *   库里老单带着 4:3,过不了这把尺子,人又被送回商品主图。
 *   别在别处再抄一份白名单 —— 抄的那份迟早跟这把尺子分叉。
 */
export function isPresetRatio(v: unknown): v is PresetRatio {
  return typeof v === "string" && RATIO_SET.has(v);
}
const USAGE_SET = new Set<string>(LONG_IMAGE_USAGES);
const REPORTER_GENRE_SET = new Set<string>(REPORTER_GENRES);
const WIDE_LAYOUT_SET = new Set<string>(WIDE_LAYOUT_KEYS);
const WIDE_PRESET_SET = new Set<string>(WIDE_PRESET_KEYS);

function parseGroupPoints(v: unknown): PresetGroupPoint[] | null {
  if (!Array.isArray(v)) return null;
  const out: PresetGroupPoint[] = [];
  for (const p of v) {
    if (!p || typeof p !== "object") return null;
    const o = p as Record<string, unknown>;
    if (typeof o.title !== "string" || !o.title.trim()) return null;
    if (o.detail !== undefined && typeof o.detail !== "string") return null;
    out.push({ title: o.title, ...(o.detail ? { detail: o.detail } : {}) });
  }
  return out;
}

/**
 * location.state 逐字段验型;形状不对返回 null(整包丢弃)。
 * 注意入参是整个 location.state(内取 .scenePreset),与 parsePosterExplosiveState 同姿势。
 */
export function parseScenePreset(state: unknown): ScenePreset | null {
  if (!state || typeof state !== "object") return null;
  const raw = (state as { scenePreset?: unknown }).scenePreset;
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  switch (o.kind) {
    case "poster_single":
      return { kind: "poster_single" };
    case "tryon":
      return { kind: "tryon" };
    case "dh_tts": {
      if (o.presetVoice !== undefined && typeof o.presetVoice !== "string") return null;
      if (o.voiceProfileId !== undefined && typeof o.voiceProfileId !== "number") return null;
      return {
        kind: "dh_tts",
        ...(o.presetVoice ? { presetVoice: o.presetVoice } : {}),
        ...(typeof o.voiceProfileId === "number" ? { voiceProfileId: o.voiceProfileId } : {}),
      };
    }
    case "dh_photo": {
      if (o.presetAvatarId !== undefined && typeof o.presetAvatarId !== "string") return null;
      if (o.presetVoice !== undefined && typeof o.presetVoice !== "string") return null;
      if (o.voiceProfileId !== undefined && typeof o.voiceProfileId !== "number") return null;
      return {
        kind: "dh_photo",
        ...(o.presetAvatarId ? { presetAvatarId: o.presetAvatarId } : {}),
        ...(o.presetVoice ? { presetVoice: o.presetVoice } : {}),
        ...(typeof o.voiceProfileId === "number" ? { voiceProfileId: o.voiceProfileId } : {}),
      };
    }
    case "video": {
      // purpose 必须 ∈ 8 目的单一源:location.state 可被伪造,而 purpose 是原文进引擎的
      // (未命中关键词会静默兜底成「产品带货」档)—— 这里不放行,比在引擎那边悄悄降级好。
      if (!isBriefPurpose(o.purpose)) return null;
      return { kind: "video", purpose: o.purpose };
    }
    case "dh_talking": {
      if (o.presetAvatarId !== undefined && typeof o.presetAvatarId !== "string") return null;
      if (o.presetVoice !== undefined && typeof o.presetVoice !== "string") return null;
      if (o.voiceProfileId !== undefined && typeof o.voiceProfileId !== "number") return null;
      return {
        kind: "dh_talking",
        ...(o.presetAvatarId ? { presetAvatarId: o.presetAvatarId } : {}),
        ...(o.presetVoice ? { presetVoice: o.presetVoice } : {}),
        ...(typeof o.voiceProfileId === "number" ? { voiceProfileId: o.voiceProfileId } : {}),
      };
    }
    case "poster_group": {
      const points = parseGroupPoints(o.groupPoints);
      return points ? { kind: "poster_group", groupPoints: points } : null;
    }
    case "poster_multiratio": {
      if (!Array.isArray(o.ratios)) return null;
      if (!o.ratios.every(isPresetRatio)) {
        return null;
      }
      // 先去重再验数量:重复项只算一个画幅,去重后 <2 = 套图语义不成立,整包丢弃
      const unique = [...new Set(o.ratios)];
      if (unique.length < 2) return null;
      return { kind: "poster_multiratio", ratios: unique };
    }
    case "longimage": {
      if (typeof o.usage !== "string" || !USAGE_SET.has(o.usage)) return null;
      if (o.reporterSeed !== undefined) {
        const seed = parseLongImageReporterSeed(o.reporterSeed);
        if (!seed) return null; // 给了种子就得是对的形,不静默丢种子降级成空骨架
        return { kind: "longimage", usage: o.usage as LongImageUsage, reporterSeed: seed };
      }
      return { kind: "longimage", usage: o.usage as LongImageUsage };
    }
    case "poster_wide": {
      if (typeof o.wideLayout !== "string" || !WIDE_LAYOUT_SET.has(o.wideLayout)) return null;
      if (typeof o.widePreset !== "string" || !WIDE_PRESET_SET.has(o.widePreset)) return null;
      return {
        kind: "poster_wide",
        wideLayout: o.wideLayout as WideLayoutKey,
        widePreset: o.widePreset as WidePresetKey,
      };
    }
    case "dh_from_video": {
      if (typeof o.script !== "string" || !o.script.trim()) return null;
      return { kind: "dh_from_video", script: o.script };
    }
    case "reporter": {
      if (typeof o.genre !== "string" || !REPORTER_GENRE_SET.has(o.genre)) return null;
      if (o.subType !== undefined && (typeof o.subType !== "string" || !o.subType.trim())) {
        return null;
      }
      if (o.sourceLabel !== undefined && typeof o.sourceLabel !== "string") return null;
      if (
        o.sourceDraftId !== undefined &&
        (typeof o.sourceDraftId !== "number" || !Number.isFinite(o.sourceDraftId))
      ) {
        return null;
      }
      if (
        o.blueprintId !== undefined &&
        (typeof o.blueprintId !== "number" || !Number.isSafeInteger(o.blueprintId))
      ) {
        return null;
      }
      if (o.formulaName !== undefined && typeof o.formulaName !== "string") return null;
      if (o.sourcePlatform !== undefined && typeof o.sourcePlatform !== "string") return null;
      let seed: ReporterMaterialSeed | undefined;
      if (o.materialSeed !== undefined) {
        const parsed = parseReporterSeed(o.materialSeed);
        if (!parsed) return null;
        seed = parsed;
      }
      let craft: ReporterBlueprintCraft | undefined;
      if (o.blueprintCraft !== undefined) {
        const parsed = parseBlueprintCraft(o.blueprintCraft);
        if (!parsed) return null;
        craft = parsed;
      }
      const sourceLabel =
        typeof o.sourceLabel === "string" && o.sourceLabel.trim()
          ? o.sourceLabel.trim()
          : undefined;
      return {
        kind: "reporter",
        genre: o.genre as ReporterPresetGenre,
        ...(o.subType ? { subType: o.subType as string } : {}),
        ...(seed ? { materialSeed: seed } : {}),
        ...(sourceLabel ? { sourceLabel } : {}),
        ...(typeof o.sourceDraftId === "number" ? { sourceDraftId: o.sourceDraftId } : {}),
        ...(typeof o.blueprintId === "number" ? { blueprintId: o.blueprintId } : {}),
        // 2026-08-14:只读展示用的蓝图 payload。**必须在这儿放行** —— 路由 state 走的就是
        // 本验型器,不列在这里的键会被静默丢掉,新闻舱那块「带给 AI 的原文」永远拿不到数据
        // (加字段时漏了这一步,被 reporterHandoff.test 的「能穿过唯一验型器」当场抓到)。
        // 只验「是个对象」:payload 是 ai 侧的自由形状,逐键验型会把新增字段挡在门外。
        ...(o.blueprintPayload && typeof o.blueprintPayload === "object"
          ? {
              blueprintPayload: o.blueprintPayload as import("@/entities/explosive").FormulaPayload,
            }
          : {}),
        // 同上:不在这儿放行的键会被静默丢掉(reporterHandoff.test 的「穿过唯一验型器」盯着)
        ...(o.blueprintBreakdown && typeof o.blueprintBreakdown === "object"
          ? {
              blueprintBreakdown:
                o.blueprintBreakdown as import("@/entities/explosive").ExplosiveBreakdown,
            }
          : {}),
        ...(typeof o.formulaName === "string" ? { formulaName: o.formulaName } : {}),
        ...(typeof o.sourcePlatform === "string" ? { sourcePlatform: o.sourcePlatform } : {}),
        // BPWB-2:同上一批字段的规矩 —— 不在这儿放行就被静默丢掉,蓝图工作卡写法四格永远空
        ...(craft ? { blueprintCraft: craft } : {}),
      };
    }
    default:
      return null;
  }
}

/** materialSeed 逐字段验型(全字段可选,但给了就得是对的形;至少一个字段非空才算种子)。 */
function parseReporterSeed(v: unknown): ReporterMaterialSeed | null {
  if (!v || typeof v !== "object") return null;
  const o = v as Record<string, unknown>;
  const out: ReporterMaterialSeed = {};
  for (const k of ["title", "userQuestion", "channel", "audience", "body"] as const) {
    const x = o[k];
    if (x !== undefined) {
      if (typeof x !== "string") return null;
      if (x.trim()) out[k] = x;
    }
  }
  if (o.keywords !== undefined) {
    if (!Array.isArray(o.keywords) || !o.keywords.every((s) => typeof s === "string")) return null;
    const kw = o.keywords.filter((s) => s.trim());
    if (kw.length) out.keywords = kw;
  }
  return Object.keys(out).length ? out : null;
}

/** blueprintCraft 逐字段验型(全字段可选,但给了就得是对的形;至少一格非空才算数,同 parseReporterSeed 姿势)。 */
function parseBlueprintCraft(v: unknown): ReporterBlueprintCraft | null {
  if (!v || typeof v !== "object") return null;
  const o = v as Record<string, unknown>;
  const out: ReporterBlueprintCraft = {};
  for (const k of ["structure", "emotion", "hookExample", "ctaExample"] as const) {
    const x = o[k];
    if (x !== undefined) {
      if (typeof x !== "string") return null;
      if (x.trim()) out[k] = x;
    }
  }
  return Object.keys(out).length ? out : null;
}

/** 官媒→长图种子验型:title 非空 + paragraphs 非空字符串数组(封顶 12 段防怪兽画布)。 */
function parseLongImageReporterSeed(v: unknown): LongImageReporterSeed | null {
  if (!v || typeof v !== "object") return null;
  const o = v as Record<string, unknown>;
  if (typeof o.title !== "string" || !o.title.trim()) return null;
  if (!Array.isArray(o.paragraphs) || !o.paragraphs.every((s) => typeof s === "string")) {
    return null;
  }
  const paragraphs = o.paragraphs.map((s) => s.trim()).filter(Boolean);
  if (!paragraphs.length) return null;
  return { title: o.title.trim(), paragraphs: paragraphs.slice(0, 12) };
}

/**
 * 消费后清 history state(React Router 把 location.state 存在 history.state.usr):
 * 刷新/回退再进本页不重放预设,不冲掉用户已改的内容。
 */
export function clearSceneCardState(): void {
  try {
    const h = window.history;
    if (h.state && typeof h.state === "object" && "usr" in h.state) {
      h.replaceState({ ...h.state, usr: undefined }, "");
    }
  } catch {
    /* 部分内核 replaceState 受限时静默;重放预设是可容忍的降级 */
  }
}
